import React, { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { buildInvoiceDocumentHtml, DEFAULT_PRO_INVOICE_ACCENT } from '../utils/invoicePdfHtml';
import {
  dashboardInputClass,
  dashboardLabelClass,
  dashboardTextareaClass,
  dashboardBtnPrimary,
  dashboardBtnModalCancel,
  dashboardModalCloseBtn,
  dashboardModalHeaderSurfaceClass,
  invoiceBuilderModalShell,
} from '../components/dashboard/DashboardChrome';
import { useTheme } from '../context/ThemeContext';
import { runReliableAsync, withTimeout, toUserMessage } from '../utils/asyncReliability';
import { devClickDebug } from '../devClickDebug';
import { devError } from '../utils/logger';
import { useResumeLoadingReset } from '../hooks/useResumeLoadingReset';

const INVOICE_MODAL_BACKDROP =
  'fixed inset-0 z-[100140] bg-slate-900/55 backdrop-blur-sm';
const INVOICE_MODAL_WRAP =
  'fixed inset-0 z-[100140] flex items-center justify-center p-1 sm:p-2';

const PREVIEW_BASE_WIDTH_PX = 720;
const PDF_GENERATION_HARD_RESET_MS = 210000;

const DEFAULT_COLUMN_LABELS = {
  description: 'Description',
  qty: 'Qty',
  rate: 'Rate',
  amount: 'Amount',
};

/** Allow empty while typing rates/amounts; only digits and one decimal point. */
function isDecimalMoneyDraft(s) {
  return s === '' || /^\d*\.?\d*$/.test(s);
}

/** Allow empty while typing quantity; whole numbers only. */
function isIntegerQtyDraft(s) {
  return s === '' || /^\d+$/.test(s);
}

function numericFieldDisplay(value, draft, idx, field, { emptyWhenZero = true } = {}) {
  if (draft && draft.idx === idx && draft.field === field) return draft.text;
  const n = parseFloat(value);
  if (!Number.isFinite(n) || (emptyWhenZero && n === 0)) return '';
  return String(value);
}

function rateInputDisplay(unitPrice, draft, idx) {
  return numericFieldDisplay(unitPrice, draft, idx, 'unitPrice');
}

function qtyInputDisplay(quantity, draft, idx) {
  return numericFieldDisplay(quantity, draft, idx, 'quantity', { emptyWhenZero: false });
}

function amountInputDisplay(total, draft, idx) {
  return numericFieldDisplay(total, draft, idx, 'total');
}

function columnVisibilityFromInvoiceData(data) {
  if (!data) return { qty: true, rate: true, amount: true };
  const hasNew =
    data.showInvoiceColQty !== undefined ||
    data.showInvoiceColRate !== undefined ||
    data.showInvoiceColAmount !== undefined;
  if (hasNew) {
    return {
      qty: data.showInvoiceColQty !== false,
      rate: data.showInvoiceColRate !== false,
      amount: data.showInvoiceColAmount !== false,
    };
  }
  const legacyOn = data.showQtyUnit !== false;
  return { qty: legacyOn, rate: legacyOn, amount: true };
}

const InvoiceModal = ({
  showInvoiceModal,
  setShowInvoiceModal,
  initialInvoiceData,
  onGeneratePDF,
  onSaveInvoiceData,
  settings = {}, // <-- add default empty object
  plan = 'free',
}) => {
  const { isDark } = useTheme();
  const isProPlan = plan === 'pro';
  // Company Info State
  const [companyLogoUrl, setCompanyLogoUrl] = useState('');
  const [companyName, setCompanyName] = useState('Your Company Name');
  const [companyStreetAddress, setCompanyStreetAddress] = useState('123 Main St');
  const [companyCity, setCompanyCity] = useState('City');
  const [companyProvince, setCompanyProvince] = useState('Province');
  const [companyPostalCode, setCompanyPostalCode] = useState('Postal Code');
  const [companyPhone, setCompanyPhone] = useState('(555) 555-5555');
  const [companyEmail, setCompanyEmail] = useState('info@company.com');
  const [companyHstNumber, setCompanyHstNumber] = useState('');
  const [showHstNumberOnPdf, setShowHstNumberOnPdf] = useState(true);
  const logoInputRef = useRef();
  const previewContainerRef = useRef(null);
  const previewInnerRef = useRef(null);
  const [previewFit, setPreviewFit] = useState({ scale: 0.55, wrapH: 640 });

  // Client/Invoice State
  const [clientInfo, setClientInfo] = useState({
    name: initialInvoiceData?.client?.name || '',
    street: initialInvoiceData?.client?.street || '',
    city: initialInvoiceData?.client?.city || '',
    province: initialInvoiceData?.client?.province || '',
    postalCode: initialInvoiceData?.client?.postalCode || '',
  });
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [issueDate, setIssueDate] = useState(new Date().toISOString().slice(0, 10));
  const [dueDate, setDueDate] = useState('');
  
  // Initialize with existing invoice data if editing
  const existingInvoiceData = initialInvoiceData?.existingInvoiceData;
  const isEditing = initialInvoiceData?.isEditing;
  
  const [lineItems, setLineItems] = useState([
    { description: '', detail: '', quantity: 1, unitPrice: 0, total: 0 },
  ]);
  /** Lets qty/rate/amount stay empty while typing; avoids number inputs snapping back. */
  const [inlineNumericDraft, setInlineNumericDraft] = useState(null);
  const [taxEnabled, setTaxEnabled] = useState(false);
  const [hstPercent, setHstPercent] = useState('');
  const [subtotal, setSubtotal] = useState(0);
  const [hstAmount, setHstAmount] = useState(0);
  const [total, setTotal] = useState(0);
  const cvInit = columnVisibilityFromInvoiceData(initialInvoiceData?.existingInvoiceData);
  const [showInvoiceColQty, setShowInvoiceColQty] = useState(cvInit.qty);
  const [showInvoiceColRate, setShowInvoiceColRate] = useState(cvInit.rate);
  const [showInvoiceColAmount, setShowInvoiceColAmount] = useState(cvInit.amount);
  /** One bottom-line total; PDF hides per-line qty/rate/amount when enabled. */
  const [manualTotalEnabled, setManualTotalEnabled] = useState(!!existingInvoiceData?.manualTotalEnabled);
  const [manualTotalInput, setManualTotalInput] = useState(() => {
    if (!existingInvoiceData?.manualTotalEnabled) return '';
    const mt = existingInvoiceData.manualTotal;
    const v =
      mt != null && String(mt).trim() !== '' && Number.isFinite(parseFloat(mt))
        ? parseFloat(mt)
        : parseFloat(existingInvoiceData.total) || 0;
    return Number.isFinite(v) ? v.toFixed(2) : '';
  });

  const useLineMultiply = showInvoiceColQty && showInvoiceColRate && !manualTotalEnabled;

  // PDF Generation state
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const pdfGenerationWatchdogRef = useRef(null);
  const clearPdfGeneratingState = useCallback(() => {
    setIsGeneratingPDF(false);
    if (pdfGenerationWatchdogRef.current) {
      clearTimeout(pdfGenerationWatchdogRef.current);
      pdfGenerationWatchdogRef.current = null;
    }
  }, []);
  useResumeLoadingReset(() => {
    devClickDebug('InvoiceModal: useResumeLoadingReset cleared isGeneratingPDF', {});
    clearPdfGeneratingState();
  });

  useEffect(() => {
    if (!showInvoiceModal) setInlineNumericDraft(null);
  }, [showInvoiceModal]);

  useEffect(
    () => () => {
      if (pdfGenerationWatchdogRef.current) {
        clearTimeout(pdfGenerationWatchdogRef.current);
        pdfGenerationWatchdogRef.current = null;
      }
    },
    []
  );

  const [noteLines, setNoteLines] = useState([{ id: Date.now(), text: '' }]);
  const [documentTitle, setDocumentTitle] = useState('Invoice');
  const [columnLabels, setColumnLabels] = useState(() => ({ ...DEFAULT_COLUMN_LABELS }));
  const [invoiceAccentColor, setInvoiceAccentColor] = useState(DEFAULT_PRO_INVOICE_ACCENT);

  useEffect(() => {
    if (!showInvoiceModal || !isProPlan) return;
    const saved = existingInvoiceData?.invoiceAccentColor;
    if (isEditing && typeof saved === 'string' && /^#[0-9A-Fa-f]{6}$/i.test(saved.trim())) {
      setInvoiceAccentColor(saved.trim());
      return;
    }
    setInvoiceAccentColor(DEFAULT_PRO_INVOICE_ACCENT);
  }, [
    showInvoiceModal,
    isProPlan,
    isEditing,
    initialInvoiceData?.quote?.id,
    existingInvoiceData?.invoiceAccentColor,
  ]);

  // Load existing invoice data when editing
  useEffect(() => {
    if (isEditing && existingInvoiceData) {
      setCompanyName(existingInvoiceData.companyInfo?.name || 'Your Company Name');
      setCompanyStreetAddress(existingInvoiceData.companyInfo?.streetAddress || '123 Main St');
      setCompanyCity(existingInvoiceData.companyInfo?.city || 'City');
      setCompanyProvince(existingInvoiceData.companyInfo?.province || 'Province');
      setCompanyPostalCode(existingInvoiceData.companyInfo?.postalCode || 'Postal Code');
      setCompanyPhone(existingInvoiceData.companyInfo?.phone || '(555) 555-5555');
      setCompanyEmail(existingInvoiceData.companyInfo?.email || 'info@company.com');
      setCompanyHstNumber(
        existingInvoiceData.companyInfo?.hstNumber ||
          existingInvoiceData.companyHstNumber ||
          settings.hstNumber ||
          ''
      );
      setShowHstNumberOnPdf(existingInvoiceData.showHstNumberOnPdf !== false);
      
      if (existingInvoiceData.companyInfo?.logoUrl) {
        setCompanyLogoUrl(existingInvoiceData.companyInfo.logoUrl);
      } else {
        setCompanyLogoUrl('');
      }
      
      setInvoiceNumber(existingInvoiceData.invoiceNumber || '');
      setIssueDate(existingInvoiceData.issueDate || new Date().toISOString().slice(0, 10));
      setDueDate(existingInvoiceData.dueDate || '');
      setManualTotalEnabled(!!existingInvoiceData.manualTotalEnabled);
      if (existingInvoiceData.manualTotalEnabled) {
        const mt = existingInvoiceData.manualTotal;
        const v =
          mt != null && String(mt).trim() !== '' && Number.isFinite(parseFloat(mt))
            ? parseFloat(mt)
            : parseFloat(existingInvoiceData.total) || 0;
        setManualTotalInput(Number.isFinite(v) ? v.toFixed(2) : '0.00');
      } else {
        setManualTotalInput('');
      }
      setTaxEnabled(existingInvoiceData.taxEnabled !== false);
      const hp = existingInvoiceData.hstPercent;
      setHstPercent(hp != null && hp !== '' ? String(hp) : '');
      {
        const v = columnVisibilityFromInvoiceData(existingInvoiceData);
        setShowInvoiceColQty(v.qty);
        setShowInvoiceColRate(v.rate);
        setShowInvoiceColAmount(v.amount);
      }
      setClientInfo({
        name: existingInvoiceData.clientInfo?.name || '',
        street: existingInvoiceData.clientInfo?.street || '',
        city: existingInvoiceData.clientInfo?.city || '',
        province: existingInvoiceData.clientInfo?.province || '',
        postalCode: existingInvoiceData.clientInfo?.postalCode || '',
      });
      // Also update line items if they exist
      if (existingInvoiceData.lineItems && existingInvoiceData.lineItems.length > 0) {
        setLineItems(
          existingInvoiceData.lineItems.map((row) => ({
            ...row,
            detail: row.detail != null ? row.detail : '',
          }))
        );
      }
      
      setDocumentTitle(
        existingInvoiceData.documentTitle != null && String(existingInvoiceData.documentTitle).trim()
          ? String(existingInvoiceData.documentTitle).trim()
          : 'Invoice'
      );
      setColumnLabels({
        ...DEFAULT_COLUMN_LABELS,
        ...(existingInvoiceData.columnLabels || {}),
      });

      if (existingInvoiceData.notesSection && Array.isArray(existingInvoiceData.notesSection.lines)) {
        const lines = existingInvoiceData.notesSection.lines;
        setNoteLines(
          lines.length > 0
            ? lines.map((l, i) => ({ id: Date.now() + i, text: l.text || '' }))
            : [{ id: Date.now(), text: '' }]
        );
      } else if (existingInvoiceData.headerSection) {
        const hs = existingInvoiceData.headerSection;
        const merged = [];
        if (hs.title && String(hs.title).trim()) merged.push({ id: Date.now(), text: hs.title });
        if (Array.isArray(hs.bulletPoints)) {
          hs.bulletPoints.forEach((p) => {
            if (p && String(p.text || '').trim()) merged.push({ id: Date.now() + Math.random(), text: p.text });
          });
        }
        setNoteLines(merged.length > 0 ? merged : [{ id: Date.now(), text: '' }]);
      }
    } else if (!isEditing && initialInvoiceData?.client) {
      setClientInfo({
        name: initialInvoiceData.client.name || '',
        street: initialInvoiceData.client.street || '',
        city: initialInvoiceData.client.city || '',
        province: initialInvoiceData.client.province || '',
        postalCode: initialInvoiceData.client.postalCode || '',
      });
      // For new invoices, start with empty line items (let user input everything)
      // Only pre-populate client and company info
      setLineItems([{ description: '', detail: '', quantity: 1, unitPrice: 0, total: 0 }]);
      
      setNoteLines([{ id: Date.now(), text: '' }]);
      const q = initialInvoiceData.quote;
      if (q) {
        setTaxEnabled(q.taxEnabled !== false);
        const tr = q.taxRate;
        setHstPercent(tr != null && tr !== '' ? String(tr) : '');
      } else {
        setTaxEnabled(false);
        setHstPercent('');
      }
      {
        const v = columnVisibilityFromInvoiceData(null);
        setShowInvoiceColQty(v.qty);
        setShowInvoiceColRate(v.rate);
        setShowInvoiceColAmount(v.amount);
      }
    }
  }, [isEditing, existingInvoiceData, initialInvoiceData, settings?.hstNumber]);

  // Auto-fill company info from settings if not editing
  useEffect(() => {
    if (!isEditing) {
      setCompanyName(settings.companyName || 'Your Company Name');
      setCompanyStreetAddress(settings.streetAddress || '123 Main St');
      setCompanyCity(settings.city || 'City');
      setCompanyProvince(settings.province || 'Province');
      setCompanyPostalCode(settings.postalCode || 'Postal Code');
      setCompanyPhone(settings.companyPhone || '(555) 555-5555');
      setCompanyEmail(settings.companyEmail || 'info@company.com');
      setCompanyHstNumber(settings.hstNumber || '');
      setShowHstNumberOnPdf((settings.hstNumber || '').trim() !== '');
      if (settings.companyLogo) {
        setCompanyLogoUrl(settings.companyLogo);
      } else {
        setCompanyLogoUrl('');
      }
    }
  }, [isEditing, settings]);

  useLayoutEffect(() => {
    if (manualTotalEnabled) {
      const t = parseFloat(manualTotalInput);
      const sub = Number.isFinite(t) ? t : 0;
      setSubtotal(sub);
      if (!taxEnabled) {
        setHstAmount(0);
        setTotal(sub);
        return;
      }
      const percent = parseFloat(hstPercent) || 0;
      const hstVal = sub * (percent / 100);
      setHstAmount(hstVal);
      setTotal(sub + hstVal);
      return;
    }
    const sub = lineItems.reduce(
      (sum, item) => sum + (useLineMultiply ? item.quantity * item.unitPrice : item.total || 0),
      0
    );
    setSubtotal(sub);
    if (!taxEnabled) {
      setHstAmount(0);
      setTotal(sub);
      return;
    }
    const percent = parseFloat(hstPercent) || 0;
    const hstVal = sub * (percent / 100);
    setHstAmount(hstVal);
    setTotal(sub + hstVal);
  }, [lineItems, hstPercent, useLineMultiply, taxEnabled, manualTotalEnabled, manualTotalInput]);

  const previewHtml = useMemo(() => {
    let subtotalP = 0;
    let hstP = 0;
    let totalP = 0;
    if (manualTotalEnabled) {
      const mt = parseFloat(manualTotalInput);
      subtotalP = Number.isFinite(mt) ? mt : 0;
      if (!taxEnabled) {
        hstP = 0;
        totalP = subtotalP;
      } else {
        const percent = parseFloat(hstPercent) || 0;
        hstP = subtotalP * (percent / 100);
        totalP = subtotalP + hstP;
      }
    } else {
      subtotalP = lineItems.reduce(
        (sum, item) => sum + (useLineMultiply ? item.quantity * item.unitPrice : item.total || 0),
        0
      );
      if (!taxEnabled) {
        hstP = 0;
        totalP = subtotalP;
      } else {
        const percent = parseFloat(hstPercent) || 0;
        hstP = subtotalP * (percent / 100);
        totalP = subtotalP + hstP;
      }
    }
    const payload = {
      companyInfo: {
        name: companyName,
        streetAddress: companyStreetAddress,
        city: companyCity,
        province: companyProvince,
        postalCode: companyPostalCode,
        phone: companyPhone,
        email: companyEmail,
        hstNumber: companyHstNumber,
        logoUrl: companyLogoUrl,
      },
      clientInfo,
      invoiceNumber,
      issueDate,
      dueDate,
      lineItems,
      taxEnabled,
      hstPercent,
      hstAmount: hstP,
      subtotal: subtotalP,
      total: totalP,
      showInvoiceColQty,
      showInvoiceColRate,
      showInvoiceColAmount,
      showQtyUnit: showInvoiceColQty && showInvoiceColRate,
      manualTotalEnabled,
      manualTotal: manualTotalEnabled ? parseFloat(manualTotalInput) || 0 : undefined,
      notesSection: { lines: noteLines.filter((line) => line.text && line.text.trim()) },
      documentTitle,
      columnLabels,
      ...(isProPlan ? { invoiceAccentColor } : {}),
      showHstNumberOnPdf,
    };
    return buildInvoiceDocumentHtml(payload, companyLogoUrl || '', {
      includeBrandingFooter: !isProPlan,
      accentColor: isProPlan ? invoiceAccentColor : undefined,
      previewTheme: isDark ? 'dark' : 'light',
    });
  }, [
    companyName,
    companyStreetAddress,
    companyCity,
    companyProvince,
    companyPostalCode,
    companyPhone,
    companyEmail,
    companyHstNumber,
    companyLogoUrl,
    clientInfo,
    invoiceNumber,
    issueDate,
    dueDate,
    lineItems,
    taxEnabled,
    hstPercent,
    showInvoiceColQty,
    showInvoiceColRate,
    showInvoiceColAmount,
    useLineMultiply,
    manualTotalEnabled,
    manualTotalInput,
    noteLines,
    documentTitle,
    columnLabels,
    isProPlan,
    invoiceAccentColor,
    showHstNumberOnPdf,
    isDark,
  ]);

  const recomputePreviewFit = useCallback(() => {
    const c = previewContainerRef.current;
    const inner = previewInnerRef.current;
    if (!c || !inner) return;
    const cw = Math.max(0, c.clientWidth - 16);
    if (cw < 48) return;
    const s = Math.max(0.38, Math.min(1, cw / PREVIEW_BASE_WIDTH_PX));
    const h = inner.scrollHeight || 800;
    setPreviewFit({ scale: s, wrapH: Math.ceil(h * s) + 4 });
  }, []);

  useLayoutEffect(() => {
    if (!showInvoiceModal) return undefined;
    const run = () => {
      requestAnimationFrame(() => recomputePreviewFit());
    };
    run();
    const c = previewContainerRef.current;
    const inner = previewInnerRef.current;
    if (!c) return undefined;
    const roC = new ResizeObserver(run);
    roC.observe(c);
    let roI;
    if (inner) {
      roI = new ResizeObserver(run);
      roI.observe(inner);
    }
    return () => {
      roC.disconnect();
      roI?.disconnect();
    };
  }, [showInvoiceModal, previewHtml, recomputePreviewFit]);

  const useCompanyLogoFromSettings = () => {
    if (settings?.companyLogo) setCompanyLogoUrl(settings.companyLogo);
  };

  /** Same snapshot used for PDF generation and for persisting a draft when closing the modal. */
  const buildCurrentInvoiceData = () => ({
    companyInfo: {
      name: companyName,
      streetAddress: companyStreetAddress,
      city: companyCity,
      province: companyProvince,
      postalCode: companyPostalCode,
      phone: companyPhone,
      email: companyEmail,
      hstNumber: companyHstNumber,
      logoUrl: companyLogoUrl,
    },
    clientInfo,
    invoiceNumber,
    issueDate,
    dueDate,
    lineItems,
    taxEnabled,
    hstPercent,
    hstAmount,
    subtotal,
    total,
    showInvoiceColQty,
    showInvoiceColRate,
    showInvoiceColAmount,
    showQtyUnit: showInvoiceColQty && showInvoiceColRate,
    notesSection: {
      lines: noteLines.filter((line) => line.text && line.text.trim()),
    },
    documentTitle,
    columnLabels,
    manualTotalEnabled,
    // Firestore rejects `undefined`; omit manualTotal when not using that mode
    ...(manualTotalEnabled
      ? { manualTotal: parseFloat(String(manualTotalInput).trim()) || 0 }
      : {}),
    ...(isProPlan &&
    typeof invoiceAccentColor === 'string' &&
    /^#[0-9A-Fa-f]{6}$/i.test(invoiceAccentColor.trim())
      ? { invoiceAccentColor: invoiceAccentColor.trim() }
      : {}),
    showHstNumberOnPdf,
  });

  const handleCloseModal = () => {
    clearPdfGeneratingState();
    const quoteId = initialInvoiceData?.quote?.id;
    if (onSaveInvoiceData && quoteId) {
      const invoiceData = buildCurrentInvoiceData();
      void onSaveInvoiceData(quoteId, invoiceData, undefined).catch((e) => {
        devError('Error saving invoice draft on close:', e);
      });
    }
    setShowInvoiceModal(false);
  };

  // Handle logo upload with compression
  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (ev) => {
        const originalLogo = ev.target.result;
        if (originalLogo.length > 500000) {
          const img = new Image();
          img.onload = () => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            
            // Calculate new dimensions (reduce size)
            const maxWidth = 200;
            const maxHeight = 200;
            let { width, height } = img;
            
            if (width > height) {
              if (width > maxWidth) {
                height = (height * maxWidth) / width;
                width = maxWidth;
              }
            } else {
              if (height > maxHeight) {
                width = (width * maxHeight) / height;
                height = maxHeight;
              }
            }
            
            canvas.width = width;
            canvas.height = height;
            
            // Draw and compress
            ctx.drawImage(img, 0, 0, width, height);
            const compressedLogo = canvas.toDataURL('image/jpeg', 0.7);
            setCompanyLogoUrl(compressedLogo);
          };
          img.src = originalLogo;
        } else {
          // Logo is small enough, use as is
          setCompanyLogoUrl(originalLogo);
        }
      };
      reader.onerror = (error) => {
        devError('Error reading logo file:', error);
        alert('Error reading logo file. Please try again.');
      };
      reader.readAsDataURL(file);
    }
  };

  const handleGeneratePDF = async () => {
    devClickDebug('InvoiceModal: handleGeneratePDF start', { isGeneratingPDF, manualTotalEnabled });
    if (isGeneratingPDF) {
      devClickDebug('InvoiceModal: handleGeneratePDF early return — already generating', {});
      return;
    }
    if (manualTotalEnabled) {
      const trimmed = String(manualTotalInput ?? '').trim();
      const mt = parseFloat(trimmed);
      if (trimmed === '' || !Number.isFinite(mt) || mt < 0) {
        devClickDebug('InvoiceModal: handleGeneratePDF early return — invalid manual total', {});
        alert('Please enter a valid invoice total (0 or greater).');
        return;
      }
    }
    devClickDebug('InvoiceModal: setIsGeneratingPDF(true)', {});
    setIsGeneratingPDF(true);
    if (pdfGenerationWatchdogRef.current) {
      clearTimeout(pdfGenerationWatchdogRef.current);
    }
    pdfGenerationWatchdogRef.current = setTimeout(() => {
      devClickDebug('InvoiceModal: watchdog reset fired for isGeneratingPDF', {});
      clearPdfGeneratingState();
    }, PDF_GENERATION_HARD_RESET_MS);

    const quoteId = initialInvoiceData?.quote?.id;
    const invoiceData = buildCurrentInvoiceData();

    try {
      if (onSaveInvoiceData && quoteId) {
        const saveResult = await runReliableAsync({
          name: 'invoiceModal:saveDraft',
          timeoutMs: 55000,
          retries: 1,
          fn: async () =>
            withTimeout(Promise.resolve(onSaveInvoiceData(quoteId, invoiceData, undefined)), 55000, 'saveInvoiceDraft'),
        });
        if (!saveResult.ok) {
          throw new Error(saveResult.userMessage || 'Could not save invoice draft.');
        }
      }

      const pdfResult = await runReliableAsync({
        name: 'invoiceModal:generatePdf',
        timeoutMs: 180000,
        retries: 0,
        fn: async () =>
          withTimeout(Promise.resolve(onGeneratePDF(invoiceData)), 180000, 'generateInvoicePdf'),
      });
      if (!pdfResult.ok) {
        throw new Error(pdfResult.userMessage || 'Could not generate the PDF.');
      }

      setShowInvoiceModal(false);
    } catch (error) {
      devClickDebug('InvoiceModal: handleGeneratePDF catch', { err: String(error) });
      devError('Error saving invoice or generating PDF:', error);
      alert(toUserMessage(error, 'Could not save or generate the PDF. Please try again.'));
    } finally {
      devClickDebug('InvoiceModal: handleGeneratePDF finally setIsGeneratingPDF(false)', {});
      clearPdfGeneratingState();
    }
  };

  if (!showInvoiceModal) return null;

  const sectionCard =
    'rounded-2xl border border-slate-200/90 bg-white p-5 shadow-sm dark:border-slate-700 dark:bg-slate-800/50';
  const sectionTitle = 'text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500';
  const sectionHeading = 'text-base font-bold text-slate-900 dark:text-slate-100';

  const lineItemNumericInputClass = `${dashboardInputClass} box-border w-full min-w-0 !py-1.5 !px-1 text-sm font-medium text-right tabular-nums disabled:bg-slate-100`;

  return ReactDOM.createPortal(
    <>
      <div className={INVOICE_MODAL_BACKDROP} aria-hidden />
      <div className={INVOICE_MODAL_WRAP}>
        <div
          className={invoiceBuilderModalShell}
          role="dialog"
          aria-modal="true"
          aria-labelledby="invoice-modal-title"
        >
          <header className={`${dashboardModalHeaderSurfaceClass} px-5 py-4 sm:px-6 sm:py-5`}>
            <div className="pointer-events-none absolute -right-10 -top-10 h-32 w-32 rounded-full bg-cyan-400/10 blur-2xl" />
            <div className="pointer-events-none absolute -bottom-8 -left-6 h-24 w-24 rounded-full bg-primary/5 blur-2xl" />
            <div className="relative flex items-start justify-between gap-4">
              <div className="min-w-0">
                <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Invoices</p>
                <h3 id="invoice-modal-title" className="mt-1 text-xl font-bold tracking-tight text-slate-900 sm:text-2xl dark:text-slate-100">
                  {isEditing ? 'Edit invoice' : 'Create invoice'}
                </h3>
                <p className="mt-1 max-w-xl text-sm text-slate-600 hidden sm:block">
                  {isEditing
                    ? 'Update details and regenerate your PDF when you are ready.'
                    : 'Fill in the details below — preview updates as you type.'}
                </p>
              </div>
              <button
                type="button"
                onClick={handleCloseModal}
                className={`${dashboardModalCloseBtn} shrink-0`}
                title="Close"
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </header>

          <div className="flex min-h-0 flex-1 flex-col overflow-hidden lg:flex-row">
            <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto border-slate-200 bg-gradient-to-b from-slate-50/95 to-slate-50 lg:flex-[3] lg:border-r dark:border-slate-700 dark:from-slate-900 dark:to-slate-950">
              <div className="space-y-5 p-5 sm:p-6">
              {isProPlan && (
                <div className={sectionCard}>
                  <p className={sectionTitle}>Appearance</p>
                  <h4 className={`${sectionHeading} mt-1 mb-3`}>Invoice accent colour</h4>
                  <div className="flex flex-wrap items-end gap-4">
                    <div>
                      <label className={dashboardLabelClass} htmlFor="invoice-accent-color">
                        Accent
                      </label>
                      <div className="mt-1 flex flex-wrap items-center gap-2">
                        <input
                          id="invoice-accent-color"
                          type="color"
                          value={/^#[0-9A-Fa-f]{6}$/i.test(invoiceAccentColor) ? invoiceAccentColor : DEFAULT_PRO_INVOICE_ACCENT}
                          onChange={(e) => setInvoiceAccentColor(e.target.value)}
                          className="h-10 w-14 cursor-pointer overflow-hidden rounded-lg border border-slate-200 bg-white p-0 shadow-sm"
                          title="Choose accent colour"
                        />
                        <input
                          type="text"
                          value={invoiceAccentColor}
                          onChange={(e) => setInvoiceAccentColor(e.target.value)}
                          onBlur={() => {
                            const raw = String(invoiceAccentColor).trim();
                            const withHash = raw.startsWith('#') ? raw : `#${raw}`;
                            if (/^#[0-9A-Fa-f]{6}$/i.test(withHash)) {
                              setInvoiceAccentColor(withHash.toLowerCase());
                            } else {
                              setInvoiceAccentColor(DEFAULT_PRO_INVOICE_ACCENT);
                            }
                          }}
                          className={`${dashboardInputClass} w-[7.5rem] font-mono text-sm`}
                          spellCheck={false}
                          aria-label="Accent colour hex"
                        />
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setInvoiceAccentColor(DEFAULT_PRO_INVOICE_ACCENT)}
                      className={dashboardBtnModalCancel}
                    >
                      Reset to neutral
                    </button>
                  </div>
                </div>
              )}

              <div className={sectionCard}>
                <p className={sectionTitle}>Your business</p>
                <h4 className={`${sectionHeading} mt-1 mb-4`}>Company &amp; logo</h4>
                <div className="flex flex-col items-start w-full">
                  <label className={dashboardLabelClass}>Logo for this invoice</label>
                  <p className="mb-3 text-xs text-slate-500">Use Settings, upload once, or skip.</p>
                  <div className="mb-3 flex flex-wrap gap-2">
                    <button
                      type="button"
                      disabled={!settings?.companyLogo}
                      title={!settings?.companyLogo ? 'Add a logo in Settings first' : ''}
                      onClick={useCompanyLogoFromSettings}
                      className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      From settings
                    </button>
                    <button
                      type="button"
                      onClick={() => logoInputRef.current && logoInputRef.current.click()}
                      className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm transition-colors hover:bg-slate-50"
                    >
                      Upload
                    </button>
                    <button
                      type="button"
                      onClick={() => setCompanyLogoUrl('')}
                      className="rounded-xl border border-slate-100 px-3 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50"
                    >
                      No logo
                    </button>
                    <input
                      ref={logoInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/jpg,image/webp"
                      className="hidden"
                      onChange={handleLogoChange}
                    />
                  </div>
                  {companyLogoUrl && (
                    <div className="mb-3 inline-block max-w-full rounded-xl border border-slate-200 bg-slate-50 p-2">
                      <div className="mb-1 text-xs text-slate-600">Preview</div>
                      <img
                        src={companyLogoUrl}
                        alt=""
                        className="max-h-20 max-w-[140px] rounded-lg border border-slate-200 object-contain"
                      />
                    </div>
                  )}
                  <div className="mb-2 grid gap-2 sm:grid-cols-2">
                    <div>
                      <label className={dashboardLabelClass}>Company name</label>
                      <input
                        type="text"
                        value={companyName}
                        onChange={(e) => setCompanyName(e.target.value)}
                        placeholder="Company name"
                        className={`${dashboardInputClass} font-semibold text-slate-900`}
                      />
                    </div>
                    <div>
                      <label className={dashboardLabelClass}>Phone</label>
                      <input
                        type="text"
                        value={companyPhone}
                        onChange={(e) => setCompanyPhone(e.target.value)}
                        placeholder="Phone"
                        className={dashboardInputClass}
                      />
                    </div>
                  </div>
                  <div className="mb-2">
                    <label className={dashboardLabelClass}>Street</label>
                    <input
                      type="text"
                      value={companyStreetAddress || ''}
                      onChange={(e) => setCompanyStreetAddress(e.target.value)}
                      placeholder="Street address"
                      className={dashboardInputClass}
                    />
                  </div>
                  <div className="mb-2 grid grid-cols-3 gap-2">
                    <div className="col-span-3 sm:col-span-1">
                      <label className={dashboardLabelClass}>City</label>
                      <input
                        type="text"
                        value={companyCity || ''}
                        onChange={(e) => setCompanyCity(e.target.value)}
                        placeholder="City"
                        className={dashboardInputClass}
                      />
                    </div>
                    <div className="col-span-3 sm:col-span-1">
                      <label className={dashboardLabelClass}>Province</label>
                      <input
                        type="text"
                        value={companyProvince || ''}
                        onChange={(e) => setCompanyProvince(e.target.value)}
                        placeholder="Province"
                        className={dashboardInputClass}
                      />
                    </div>
                    <div className="col-span-3 sm:col-span-1">
                      <label className={dashboardLabelClass}>Postal</label>
                      <input
                        type="text"
                        value={companyPostalCode || ''}
                        onChange={(e) => setCompanyPostalCode(e.target.value)}
                        placeholder="Postal code"
                        className={dashboardInputClass}
                      />
                    </div>
                  </div>
                  <div>
                    <label className={dashboardLabelClass}>Email</label>
                    <input
                      type="email"
                      value={companyEmail}
                      onChange={(e) => setCompanyEmail(e.target.value)}
                      placeholder="Email"
                      className={dashboardInputClass}
                    />
                  </div>
                  <div className="mt-2">
                    <label className={dashboardLabelClass}>HST No.</label>
                    <input
                      type="text"
                      value={companyHstNumber}
                      onChange={(e) => setCompanyHstNumber(e.target.value)}
                      placeholder="123456789 RT0001"
                      className={dashboardInputClass}
                    />
                  </div>
                </div>
              </div>

              <div className={sectionCard}>
                <p className={sectionTitle}>Client &amp; dates</p>
                <div className="mt-3 grid gap-5 lg:grid-cols-2">
                  <div className="min-w-0 rounded-xl border border-slate-100 bg-slate-50/80 p-4">
                    <h4 className="text-sm font-bold text-slate-900">Bill to</h4>
                    <p className="mt-2 font-medium text-slate-800">{clientInfo.name || '—'}</p>
                    <p className="mt-1 text-sm text-slate-600">{clientInfo.street}</p>
                    <p className="text-sm text-slate-600">
                      {[clientInfo.city, clientInfo.province, clientInfo.postalCode].filter(Boolean).join(', ') || '—'}
                    </p>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div>
                      <label className={dashboardLabelClass}>Invoice #</label>
                      <input
                        type="text"
                        value={invoiceNumber}
                        onChange={(e) => setInvoiceNumber(e.target.value)}
                        className={dashboardInputClass}
                        placeholder="Auto"
                      />
                    </div>
                    <div>
                      <label className={dashboardLabelClass}>Issued</label>
                      <input
                        type="date"
                        value={issueDate}
                        onChange={(e) => setIssueDate(e.target.value)}
                        className={dashboardInputClass}
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label className={dashboardLabelClass}>Due</label>
                      <input
                        type="date"
                        value={dueDate}
                        onChange={(e) => setDueDate(e.target.value)}
                        className={dashboardInputClass}
                      />
                    </div>
                    <div className="sm:col-span-2 space-y-2 rounded-xl border border-slate-100 bg-white px-3 py-2.5">
                      <p className="text-xs font-medium text-slate-600">Columns on PDF</p>
                      <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-700">
                        <input
                          type="checkbox"
                          checked={showInvoiceColQty}
                          onChange={(e) => setShowInvoiceColQty(e.target.checked)}
                          className="rounded border-slate-300 text-primary focus:ring-primary/30"
                        />
                        <span>Quantity</span>
                      </label>
                      <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-700">
                        <input
                          type="checkbox"
                          checked={showInvoiceColRate}
                          onChange={(e) => setShowInvoiceColRate(e.target.checked)}
                          className="rounded border-slate-300 text-primary focus:ring-primary/30"
                        />
                        <span>Unit price / rate</span>
                      </label>
                      <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-700">
                        <input
                          type="checkbox"
                          checked={showInvoiceColAmount}
                          onChange={(e) => setShowInvoiceColAmount(e.target.checked)}
                          className="rounded border-slate-300 text-primary focus:ring-primary/30"
                        />
                        <span>Amount</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              <div className={sectionCard}>
                <p className={sectionTitle}>PDF appearance</p>
                <h4 className={`${sectionHeading} mt-1 mb-4`}>Title &amp; column labels</h4>
                <label className={dashboardLabelClass}>Document title (above company block)</label>
                <input
                  type="text"
                  value={documentTitle}
                  onChange={(e) => setDocumentTitle(e.target.value)}
                  placeholder="Invoice"
                  className={`${dashboardInputClass} mb-4`}
                />
                <p className="mb-2 text-xs font-medium text-slate-600">Table headers</p>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-xs text-slate-500">First column</label>
                    <input
                      type="text"
                      value={columnLabels.description}
                      onChange={(e) => setColumnLabels((prev) => ({ ...prev, description: e.target.value }))}
                      className={dashboardInputClass}
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs text-slate-500">Quantity (header)</label>
                    <input
                      type="text"
                      value={columnLabels.qty}
                      onChange={(e) => setColumnLabels((prev) => ({ ...prev, qty: e.target.value }))}
                      className={dashboardInputClass}
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs text-slate-500">Unit price (header)</label>
                    <input
                      type="text"
                      value={columnLabels.rate}
                      onChange={(e) => setColumnLabels((prev) => ({ ...prev, rate: e.target.value }))}
                      className={dashboardInputClass}
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs text-slate-500">Amount (header)</label>
                    <input
                      type="text"
                      value={columnLabels.amount}
                      onChange={(e) => setColumnLabels((prev) => ({ ...prev, amount: e.target.value }))}
                      className={dashboardInputClass}
                    />
                  </div>
                </div>
                <p className="mt-2 text-xs text-slate-500">
                  Column checkboxes under Client &amp; dates control which of these appear on the PDF.
                </p>
                <div className="mt-4 rounded-xl border border-slate-100 bg-slate-50/70 px-3 py-2.5">
                  <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-700">
                    <input
                      type="checkbox"
                      checked={showHstNumberOnPdf}
                      onChange={(e) => setShowHstNumberOnPdf(e.target.checked)}
                      className="rounded border-slate-300 text-primary focus:ring-primary/30"
                    />
                    <span>Show HST No. on PDF</span>
                  </label>
                </div>
              </div>

              <div className={sectionCard}>
                <p className={sectionTitle}>Line items</p>
                <h4 className={`${sectionHeading} mt-1 mb-3`}>Work &amp; amounts</h4>
                <div className="rounded-xl border border-slate-200">
                <table className="w-full table-fixed border-collapse text-sm">
                  <colgroup>
                    <col />
                    {showInvoiceColQty && <col style={{ width: '3.25rem' }} />}
                    {showInvoiceColRate && <col style={{ width: '5.5rem' }} />}
                    {showInvoiceColAmount && <col style={{ width: '6rem' }} />}
                  </colgroup>
                  <thead>
                    <tr className="bg-slate-900 text-left text-xs font-semibold uppercase tracking-wide text-white">
                      <th className="px-2 py-2">{columnLabels.description || 'Description'}</th>
                      {showInvoiceColQty && (
                        <th className="px-1 py-2 text-right text-[10px] normal-case tracking-normal">
                          {columnLabels.qty || 'Qty'}
                        </th>
                      )}
                      {showInvoiceColRate && (
                        <th className="px-1 py-2 text-right text-[10px] normal-case tracking-normal">
                          {columnLabels.rate || 'Rate'}
                        </th>
                      )}
                      {showInvoiceColAmount && (
                        <th className="px-1 py-2 text-right text-[10px] normal-case tracking-normal">
                          {columnLabels.amount || 'Amount'}
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {lineItems.map((item, idx) => (
                      <tr key={idx} className="border-b border-slate-100 bg-white last:border-0">
                        <td className="align-top px-2 py-2">
                          <input
                            type="text"
                            value={item.description}
                            onChange={(e) => {
                              const newItems = [...lineItems];
                              newItems[idx].description = e.target.value;
                              setLineItems(newItems);
                            }}
                            placeholder="Line title"
                            className={`${dashboardInputClass} !py-2 text-sm`}
                          />
                          <div className="mt-1 flex items-start gap-2">
                            <textarea
                              value={item.detail ?? ''}
                              onChange={(e) => {
                                const newItems = [...lineItems];
                                newItems[idx].detail = e.target.value;
                                setLineItems(newItems);
                              }}
                              placeholder="Details (optional)"
                              rows={2}
                              className={`${dashboardTextareaClass} min-w-0 flex-1 !min-h-[3rem] !py-2 text-xs text-slate-600`}
                            />
                            {lineItems.length > 1 && (
                              <button
                                type="button"
                                onClick={() => {
                                  setInlineNumericDraft(null);
                                  setLineItems(lineItems.filter((_, i) => i !== idx));
                                }}
                                className="mt-0.5 shrink-0 rounded-lg border border-red-100 bg-red-50 p-2 text-red-600 transition-colors hover:bg-red-100"
                                title="Remove line"
                                aria-label="Remove line"
                              >
                                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            )}
                          </div>
                        </td>
                        {showInvoiceColQty && (
                          <td className="w-11 px-1 py-2 align-top">
                            <input
                              type="text"
                              inputMode="numeric"
                              autoComplete="off"
                              value={qtyInputDisplay(item.quantity, inlineNumericDraft, idx)}
                              onFocus={() => {
                                const n = Number(item.quantity);
                                setInlineNumericDraft({
                                  idx,
                                  field: 'quantity',
                                  text: !Number.isFinite(n) || n <= 0 ? '' : String(Math.floor(n)),
                                });
                              }}
                              onBlur={() => {
                                if (inlineNumericDraft?.idx !== idx || inlineNumericDraft?.field !== 'quantity') return;
                                const raw = String(inlineNumericDraft.text).trim();
                                const parsed = parseInt(raw, 10);
                                const quantity = Number.isFinite(parsed) && parsed >= 1 ? parsed : 1;
                                setLineItems((items) => {
                                  const next = [...items];
                                  if (!next[idx]) return items;
                                  next[idx] = { ...next[idx], quantity };
                                  if (showInvoiceColQty && showInvoiceColRate && !manualTotalEnabled) {
                                    next[idx].total = next[idx].quantity * next[idx].unitPrice;
                                  }
                                  return next;
                                });
                                setInlineNumericDraft(null);
                              }}
                              onChange={(e) => {
                                const text = e.target.value;
                                if (!isIntegerQtyDraft(text)) return;
                                setInlineNumericDraft({ idx, field: 'quantity', text });
                                const parsed = parseInt(text, 10);
                                const quantity = Number.isFinite(parsed) && parsed >= 1 ? parsed : text === '' ? 0 : 1;
                                setLineItems((items) => {
                                  const next = [...items];
                                  if (!next[idx]) return items;
                                  next[idx] = { ...next[idx], quantity: text === '' ? 0 : quantity };
                                  if (showInvoiceColQty && showInvoiceColRate && !manualTotalEnabled) {
                                    next[idx].total = next[idx].quantity * next[idx].unitPrice;
                                  }
                                  return next;
                                });
                              }}
                              disabled={manualTotalEnabled}
                              className={lineItemNumericInputClass}
                              placeholder="1"
                            />
                          </td>
                        )}
                        {showInvoiceColRate && (
                          <td className="px-1 py-2 align-top">
                            <input
                              type="text"
                              inputMode="decimal"
                              autoComplete="off"
                              value={rateInputDisplay(item.unitPrice, inlineNumericDraft, idx)}
                              onFocus={() => {
                                const n = parseFloat(item.unitPrice);
                                setInlineNumericDraft({
                                  idx,
                                  field: 'unitPrice',
                                  text: !Number.isFinite(n) || n === 0 ? '' : String(n),
                                });
                              }}
                              onBlur={() => {
                                if (inlineNumericDraft?.idx !== idx || inlineNumericDraft?.field !== 'unitPrice') return;
                                const raw = String(inlineNumericDraft.text).trim();
                                const parsed = parseFloat(raw);
                                const unitPrice = Number.isFinite(parsed) ? parsed : 0;
                                setLineItems((items) => {
                                  const next = [...items];
                                  if (!next[idx]) return items;
                                  next[idx] = { ...next[idx], unitPrice };
                                  if (showInvoiceColQty && showInvoiceColRate && !manualTotalEnabled) {
                                    next[idx].total = next[idx].quantity * next[idx].unitPrice;
                                  }
                                  return next;
                                });
                                setInlineNumericDraft(null);
                              }}
                              onChange={(e) => {
                                const text = e.target.value;
                                if (!isDecimalMoneyDraft(text)) return;
                                setInlineNumericDraft({ idx, field: 'unitPrice', text });
                                const parsed = parseFloat(text);
                                const unitPrice = Number.isFinite(parsed) ? parsed : 0;
                                setLineItems((items) => {
                                  const next = [...items];
                                  if (!next[idx]) return items;
                                  next[idx] = { ...next[idx], unitPrice };
                                  if (showInvoiceColQty && showInvoiceColRate && !manualTotalEnabled) {
                                    next[idx].total = next[idx].quantity * next[idx].unitPrice;
                                  }
                                  return next;
                                });
                              }}
                              disabled={manualTotalEnabled}
                              className={lineItemNumericInputClass}
                              placeholder="0"
                            />
                          </td>
                        )}
                        {showInvoiceColAmount && (
                          <td className="px-1 py-2 align-top">
                            <input
                              type="text"
                              inputMode="decimal"
                              autoComplete="off"
                              value={amountInputDisplay(item.total, inlineNumericDraft, idx)}
                              onFocus={() => {
                                const n = parseFloat(item.total);
                                setInlineNumericDraft({
                                  idx,
                                  field: 'total',
                                  text: !Number.isFinite(n) || n === 0 ? '' : String(n),
                                });
                              }}
                              onBlur={() => {
                                if (inlineNumericDraft?.idx !== idx || inlineNumericDraft?.field !== 'total') return;
                                const raw = String(inlineNumericDraft.text).trim();
                                const parsed = parseFloat(raw);
                                const totalVal = Number.isFinite(parsed) ? parsed : 0;
                                setLineItems((items) => {
                                  const next = [...items];
                                  if (!next[idx]) return items;
                                  next[idx] = { ...next[idx], total: totalVal };
                                  return next;
                                });
                                setInlineNumericDraft(null);
                              }}
                              onChange={(e) => {
                                const text = e.target.value;
                                if (!isDecimalMoneyDraft(text)) return;
                                setInlineNumericDraft({ idx, field: 'total', text });
                                const parsed = parseFloat(text);
                                const totalVal = Number.isFinite(parsed) ? parsed : 0;
                                setLineItems((items) => {
                                  const next = [...items];
                                  if (!next[idx]) return items;
                                  next[idx] = { ...next[idx], total: totalVal };
                                  return next;
                                });
                              }}
                              className={lineItemNumericInputClass}
                              disabled={useLineMultiply || manualTotalEnabled}
                              placeholder="0"
                            />
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
                <button
                  type="button"
                  className="mt-3 inline-flex items-center gap-2 rounded-xl border border-primary/40 bg-primary/5 px-4 py-2 text-sm font-semibold text-primary transition-colors hover:bg-primary/10"
                  onClick={() =>
                    setLineItems([
                      ...lineItems,
                      { description: '', detail: '', quantity: 1, unitPrice: 0, total: 0 },
                    ])
                  }
                >
                  + Add line
                </button>
              </div>

              <div className={`${sectionCard} flex w-full flex-col gap-4`}>
                <div>
                  <p className={sectionTitle}>Totals</p>
                  <h4 className={`${sectionHeading} mt-1`}>Tax &amp; final amount</h4>
                </div>
                <div className="ml-auto w-full max-w-sm space-y-2 text-right">
                  {!manualTotalEnabled ? (
                    <>
                      {taxEnabled && (
                        <div>
                          Subtotal: $
                          {subtotal.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </div>
                      )}
                      <label className="flex items-center justify-end gap-2 text-sm cursor-pointer">
                        <input
                          type="checkbox"
                          checked={taxEnabled}
                          onChange={(e) => setTaxEnabled(e.target.checked)}
                          className="rounded border-gray-300"
                        />
                        <span>Apply tax</span>
                      </label>
                      {taxEnabled && (
                        <>
                          <div className="flex items-center justify-end gap-2">
                            <span>Tax (%):</span>
                            <input
                              type="number"
                              min="0"
                              max="100"
                              step="0.01"
                              value={hstPercent}
                              onChange={(e) => setHstPercent(e.target.value)}
                              className="border rounded px-2 py-1 w-20 text-right"
                              placeholder="e.g. 13"
                            />
                          </div>
                          <div>
                            Tax: $
                            {hstAmount.toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </div>
                        </>
                      )}
                      <div className="font-bold pt-1">
                        Total: $
                        {total.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex items-center justify-end gap-2 flex-wrap">
                        <span className="text-sm">Invoice amount ($)</span>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={manualTotalInput}
                          onChange={(e) => setManualTotalInput(e.target.value)}
                          className="border rounded px-2 py-1 w-32 text-right"
                        />
                      </div>
                      <p className="text-xs text-gray-500">
                        Before tax. PDF uses dashes in quantity/amount columns.
                      </p>
                      <label className="flex items-center justify-end gap-2 text-sm cursor-pointer">
                        <input
                          type="checkbox"
                          checked={taxEnabled}
                          onChange={(e) => setTaxEnabled(e.target.checked)}
                          className="rounded border-gray-300"
                        />
                        <span>Apply tax</span>
                      </label>
                      {taxEnabled && (
                        <>
                          <div className="flex items-center justify-end gap-2">
                            <span>Tax (%):</span>
                            <input
                              type="number"
                              min="0"
                              max="100"
                              step="0.01"
                              value={hstPercent}
                              onChange={(e) => setHstPercent(e.target.value)}
                              className="border rounded px-2 py-1 w-20 text-right"
                              placeholder="e.g. 13"
                            />
                          </div>
                          <div>
                            Subtotal: $
                            {subtotal.toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </div>
                          <div>
                            Tax: $
                            {hstAmount.toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </div>
                        </>
                      )}
                      <div className="font-bold pt-1">
                        Total: $
                        {total.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </div>
                    </>
                  )}
                </div>

                <div className="ml-auto mt-2 w-full max-w-sm border-t border-slate-200 pt-4 text-right">
                  <label className="inline-flex cursor-pointer items-center justify-end gap-2 text-sm text-slate-700">
                    <input
                      type="checkbox"
                      checked={manualTotalEnabled}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setManualTotalEnabled(checked);
                        if (checked) {
                          setTaxEnabled(false);
                          const sub = lineItems.reduce((sum, item) => {
                            if (showInvoiceColQty && showInvoiceColRate) {
                              return sum + item.quantity * item.unitPrice;
                            }
                            return sum + (item.total || 0);
                          }, 0);
                          setManualTotalInput(Number.isFinite(sub) ? sub.toFixed(2) : '0.00');
                        }
                      }}
                      className="rounded border-slate-300 text-primary focus:ring-primary/30"
                    />
                    <span>Set total manually</span>
                  </label>
                  <p className="mt-1 text-xs text-slate-500">Single bottom-line amount; optional tax after.</p>
                </div>
              </div>

              <div className={sectionCard}>
                <p className={sectionTitle}>Notes</p>
                <h4 className={`${sectionHeading} mt-1 mb-3`}>Optional PDF notes</h4>
                <p className="mb-4 text-xs text-slate-500">Appear under a &quot;Notes&quot; section on the PDF.</p>
                <div className="space-y-3">
                  {noteLines.map((line, index) => (
                    <div key={line.id} className="flex items-center gap-2">
                      <span className="w-6 shrink-0 text-sm text-slate-400">{index + 1}.</span>
                      <input
                        type="text"
                        value={line.text}
                        onChange={(e) => {
                          const next = [...noteLines];
                          next[index] = { ...next[index], text: e.target.value };
                          setNoteLines(next);
                        }}
                        placeholder="Note line"
                        className={`${dashboardInputClass} flex-1`}
                      />
                      {noteLines.length > 1 && (
                        <button
                          type="button"
                          onClick={() => setNoteLines(noteLines.filter((_, i) => i !== index))}
                          className="shrink-0 rounded-lg p-1.5 text-red-600 hover:bg-red-50"
                          aria-label="Remove line"
                        >
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => setNoteLines([...noteLines, { id: Date.now() + Math.random(), text: '' }])}
                    className={`${dashboardBtnModalCancel} border-primary/30 text-primary hover:bg-primary/5`}
                  >
                    + Add note line
                  </button>
                </div>
              </div>
              </div>
            </div>

            <div className="hidden min-h-0 min-w-0 flex-1 flex-col border-t border-slate-200 bg-slate-100/90 lg:flex lg:flex-[2] lg:border-l lg:border-t-0 dark:border-slate-700 dark:bg-slate-950">
              <div className="shrink-0 border-b border-slate-200 bg-white px-4 py-3 sm:px-5 dark:border-slate-700 dark:bg-slate-900">
                <p className={sectionTitle}>Preview</p>
                <h3 className={`${sectionHeading} mt-0.5`}>PDF layout</h3>
                <p className="mt-1 text-xs text-slate-500">Matches the file you generate and download.</p>
              </div>
              <div
                ref={previewContainerRef}
                className="flex min-h-0 flex-1 justify-center overflow-auto bg-slate-200/40 p-2 sm:p-3 dark:bg-slate-950"
              >
                <div
                  className="invoice-pdf-preview-frame shrink-0 overflow-hidden rounded-xl border border-slate-200/80 bg-slate-800 shadow-md dark:border-slate-600"
                  style={{
                    width: PREVIEW_BASE_WIDTH_PX * previewFit.scale,
                    height: previewFit.wrapH,
                  }}
                >
                  <div
                    ref={previewInnerRef}
                    style={{
                      width: PREVIEW_BASE_WIDTH_PX,
                      transform: `scale(${previewFit.scale})`,
                      transformOrigin: 'top left',
                    }}
                    dangerouslySetInnerHTML={{ __html: previewHtml }}
                  />
                </div>
              </div>
            </div>
          </div>

          <footer className="flex shrink-0 items-center justify-end gap-3 border-t border-slate-200 bg-slate-50/80 px-5 py-4 sm:px-6 dark:border-slate-700 dark:bg-slate-900/90">
            <button type="button" onClick={handleCloseModal} className={dashboardBtnModalCancel}>
              Cancel
            </button>
            <button
              type="button"
              onClick={handleGeneratePDF}
              disabled={isGeneratingPDF}
              className={`${dashboardBtnPrimary} px-6 py-2.5 text-sm sm:text-base disabled:cursor-not-allowed disabled:opacity-50 disabled:shadow-none`}
            >
              {isGeneratingPDF ? 'Generating…' : isEditing ? 'Update PDF' : 'Generate PDF'}
            </button>
          </footer>
        </div>
      </div>
    </>,
    document.body
  );
};

export default InvoiceModal; 